import React, { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker'
import './TimeClockPicker.css'

interface TimeClockPickerProps {
  isCronRan: boolean
  checkInInitiatedDate: string
  checkInInitiatedTime: string
  onDateChange: (
    selectedTime: string,
    expiryTime: string,
    selectedDate: string,
  ) => void
}

const TimeClockPicker: React.FC<TimeClockPickerProps> = ({
  isCronRan,
  checkInInitiatedDate,
  checkInInitiatedTime,
  onDateChange,
}) => {
  const [defaultTime, setDefaultTime] = useState(
    checkInInitiatedDate && checkInInitiatedTime
      ? isCronRan && dayjs(checkInInitiatedDate).isSame(dayjs(), 'day')
        ? dayjs(`${checkInInitiatedTime}`).add(1, 'day')
        : dayjs(`${checkInInitiatedDate} ${checkInInitiatedTime}`)
      : dayjs(),
  )

  const [open, setOpen] = useState(false)
  const [expiryTime, setExpiryTime] = useState<string>(
    defaultTime.add(1, 'hour').format('hh:mm A'),
  )
  const [selectedDate, setSelectedDate] = useState<string>(
    defaultTime.add(1, 'hour').format('YYYY-MM-DD'),
  )
  const [selectedTime, setSelectedTime] = useState<string>(
    defaultTime.format('hh:mm A'),
  )

  const calculateDates = (initialDate: Dayjs) => {
    const newDate =
      isCronRan && dayjs(checkInInitiatedDate).isSame(dayjs(), 'day')
        ? initialDate.add(1, 'day')
        : initialDate

    const newExpiryDate = newDate.add(1, 'hour')
    setSelectedTime(newDate.format('hh:mm A'))
    setExpiryTime(newExpiryDate.format('hh:mm A'))
    setSelectedDate(newExpiryDate.format('YYYY-MM-DD'))
  }

  const handleTimeChange = (newValue: Dayjs | null) => {
    if (newValue) {
      calculateDates(newValue)
    }
  }

  const [hours, minutes, period] = selectedTime.split(/[:\s]/)

  useEffect(() => {
    if (checkInInitiatedDate && checkInInitiatedTime) {
      const newDefaultTime = dayjs(
        `${checkInInitiatedDate} ${checkInInitiatedTime}`,
      )
      setDefaultTime(newDefaultTime)
      calculateDates(newDefaultTime)
    }
  }, [checkInInitiatedDate, checkInInitiatedTime])

  useEffect(() => {
    onDateChange(selectedTime, expiryTime, selectedDate)
  }, [selectedTime, expiryTime, selectedDate, onDateChange])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MobileTimePicker
        value={defaultTime}
        onChange={handleTimeChange}
        onClose={() => setOpen(false)}
        open={open}
        sx={{ display: 'none' }}
      />
      <div
        style={{
          display: 'flex',
          gap: '8px',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={() => setOpen(true)}
      >
        <div className="flex gap-4 items-center">
          <div className="bg-grad before:rounded-[22px]">
            <div className="h-[70px] w-[70px] rounded-[22px] flex items-center justify-center font-semibold text-[20px] bg-[#10131a]">
              {hours}
            </div>
          </div>
          <div className="font-semibold text-[20px]">:</div>
          <div className="bg-grad before:rounded-[22px]">
            <div className="h-[70px] w-[70px] rounded-[22px] flex items-center justify-center font-semibold text-[20px] bg-[#10131a]">
              {minutes}
            </div>
          </div>
          <div className="bg-grad before:rounded-[22px]">
            <div className="h-[70px] w-[70px] rounded-[22px] flex items-center justify-center font-semibold text-[20px] bg-[#10131a]">
              {period}
            </div>
          </div>
        </div>
      </div>
    </LocalizationProvider>
  )
}

export default TimeClockPicker
